'use client'

import { ArrowRight } from 'lucide-react'
import { trackEvent } from '@/lib/track-event'
import { openCheckout } from '@/lib/paddle/checkout'

/**
 * "$5 unlock" checkout button shown under a teaser result. Opens the Paddle
 * overlay for the single-audit pack in place — no navigation to a pricing page.
 *
 * When `showPowerPack` is set (the /platform 13-tool teaser), a secondary
 * Power Pack option (10 audits for $40, the best-value pack) is surfaced
 * directly beneath the $5 button so repeat/volume buyers see it without opening
 * a modal. It reuses the same `openCheckout('pack-power')` path as PackModal —
 * credits are granted post-payment by the webhook, so no API spend happens
 * before payment. The /tools/* TeaserResult path leaves `showPowerPack` off and
 * renders exactly the bare $5 button as before.
 */
interface UnlockReportProps {
  priceUsd?: number
  /** Saved report id — tags the checkout so the webhook unlocks THIS report. */
  reportId?: string
  /**
   * Checkout source tag. 'tool_unlock' (default) → /tools/unlock landing.
   * 'platform_unlock' → the buyer lands in the dashboard for the full 13-tool
   * audit (requires `domain`).
   */
  source?: string
  /** Domain to audit after a platform unlock (builds the dashboard successUrl). */
  domain?: string
  /** Surface the $40 Power Pack (10 audits) beneath the $5 unlock. */
  showPowerPack?: boolean
}

export default function UnlockReport({
  priceUsd = 5,
  reportId,
  source = 'tool_unlock',
  domain,
  showPowerPack = false,
}: UnlockReportProps) {
  const entryPoint =
    source === 'platform_unlock' ? 'platform_teaser' : 'tool_teaser'

  const unlockButton = (
    <button
      type='button'
      onClick={() => {
        trackEvent('teaser_unlock_clicked', { pack: 'single' })
        openCheckout(
          'pack-single',
          entryPoint,
          reportId
            ? { reportId, source, ...(domain ? { domain } : {}) }
            : undefined
        )
      }}
      className='inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition-colors'
      style={{ backgroundColor: '#008dcc', color: '#ffffff' }}
    >
      Unlock full report + open in Radar &mdash; ${priceUsd}
      <ArrowRight size={16} />
    </button>
  )

  if (!showPowerPack) return unlockButton

  return (
    <div className='flex flex-col items-center gap-3 w-full'>
      {unlockButton}
      <div className='w-full max-w-sm border border-growth bg-growth/5 p-4 text-center'>
        <div className='flex items-center justify-center gap-2 mb-1'>
          <span className='text-sm font-bold text-foreground leading-none'>
            Power Pack
          </span>
          <span className='text-[10px] font-bold uppercase tracking-wider bg-growth text-white px-1.5 py-0.5'>
            Best value
          </span>
        </div>
        <p className='text-sm text-foreground/80'>
          10 audits for $40, just $4 per audit.
        </p>
        <p className='text-xs text-muted-foreground mt-1 mb-3'>
          Ideal for competitor checks, monthly rechecks, and client audits.
        </p>
        <button
          type='button'
          onClick={() => {
            trackEvent('teaser_unlock_clicked', { pack: 'power' })
            openCheckout('pack-power', entryPoint)
          }}
          className='inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold border border-growth text-growth hover:bg-growth hover:text-white transition-colors'
        >
          Get the Power Pack &mdash; $40
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}
